/*
 * Main Product Gallery — Swiper init + variant routing.
 *
 * Mounts on every `.product-main-gallery` element rendered by
 * snippets/product-main-gallery.liquid. The Liquid pre-renders
 * Swiper-shaped markup (.swiper > .swiper-wrapper > .swiper-slide
 * for both the main track and the thumbs strip); this script
 * builds the Swiper instances and wires them together.
 *
 * Settings come in via data-* attributes on the gallery root:
 *   data-layout            'slider' | 'scroll'  (Gallery: Layout)
 *   data-loop              'true'   | 'false'   (Loop Slider)
 *   data-autoplay          'true'   | 'false'   (Autoplay)
 *   data-autoplay-delay    milliseconds         (Autoplay Delay)
 *   data-show-arrows       'true'   | 'false'   (Show Arrows)
 *   data-show-bullets      'true'   | 'false'   (Show Pagination Bullets)
 *   data-show-thumbnails   'true'   | 'false'   (Show Thumbnails, > 1 media)
 *
 * Scroll mode (desktop only): when layout=scroll and viewport
 * ≥ 992px, the main slider stays as a vertical stack — no Swiper
 * is initialised on the main track and arrows / bullets / thumbs
 * are hidden. Mobile always swipes (Swiper still runs).
 *
 * Variant routing: listens for H3.0's `variant:update` event,
 * looks up the slide carrying `data-variant-id="{variant.id}"`,
 * and navigates the main Swiper to it. Slides without a
 * data-variant-id are media not tied to a specific variant and
 * are skipped by the lookup.
 *
 * Swiper is loaded as a UMD bundle in snippets/scripts.liquid,
 * exposing window.Swiper globally. See assets/testimonials.js for
 * the same init pattern.
 */

const Swiper = window.Swiper;

const SELECTOR = '.product-main-gallery';
const INSTANCE_KEY = '_productMainGalleryInstance';
const DESKTOP_QUERY = '(min-width: 992px)';

class ProductMainGallery {
  constructor(root) {
    this.root = root;
    this.layout = root.dataset.layout || 'slider';
    this.loop = root.dataset.loop === 'true';
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 5000;
    this.showArrows = root.dataset.showArrows === 'true';
    this.showBullets = root.dataset.showBullets === 'true';
    this.showThumbnails = root.dataset.showThumbnails === 'true';

    this.mainEl = root.querySelector('.product-main-gallery__main');
    this.thumbsEl = root.querySelector('.product-main-gallery__thumbs');
    this.prevEl = root.querySelector('.product-main-gallery__arrow--prev');
    this.nextEl = root.querySelector('.product-main-gallery__arrow--next');
    this.bulletsEl = root.querySelector('.product-main-gallery__bullets');

    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.abortController = new AbortController();

    this.init();
  }

  init() {
    if (!this.mainEl) return;
    this._bindPhotoViewer();

    const slides = this.mainEl.querySelectorAll('.swiper-slide');
    const hasMultiple = slides.length > 1;

    // Desktop scroll mode: skip Swiper entirely, leave the static
    // vertical stack in place. Mobile (< 992px) always uses Swiper
    // even when the merchant picked Scroll for desktop.
    const isDesktopScroll =
      this.layout === 'scroll' && window.matchMedia(DESKTOP_QUERY).matches;

    if (isDesktopScroll || !hasMultiple) {
      this.root.classList.remove('not-loaded');
      // .is-static drives natural-aspect height + object-fit: contain —
      // correct for the desktop scroll stack, wrong for a single-slide
      // slider where the merchant set Section Height and expects the
      // image to fill it. Single-slide slider gets .is-initialized so
      // the default fill-the-container rules apply.
      if (isDesktopScroll) {
        this.root.classList.add('is-static');
        const column = this.root.closest('.product-main__grid--left');
        if (column) column.classList.add('product-main__grid--left--scroll');
      } else {
        this.root.classList.add('is-initialized');
      }
      this._bindVariantUpdate();
      return;
    }

    // Build thumbs swiper first (so the main can bind to it).
    // centerInsufficientSlides is OFF: the design starts the strip at the
    // leading edge, so a product with two thumbs sits left on mobile rather
    // than floating in the middle. freeMode still lets the merchant scroll
    // them once they overflow.
    //
    // observer + observeParents re-measure on container size changes,
    // covering the init-on-zero-width race (slides measured as 0px
    // at init lock the slider in `watchOverflow` mode — see the main
    // Swiper config for the matching guard).
    if (this.showThumbnails && this.thumbsEl) {
      // Direction follows the layout: a row under the image below 992, a
      // column beside it above. Swiper 11 handles `direction` inside
      // breakpoints (it calls changeDirection internally), so the strip
      // re-orients on resize without a re-init. spaceBetween differs per
      // axis because the design does: 8 across, 10 down.
      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        direction: 'horizontal',
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        centerInsufficientSlides: false,
        observer: true,
        observeParents: true,
        breakpoints: {
          992: {
            direction: 'vertical',
            spaceBetween: 10,
          },
        },
      });
    }

    // watchOverflow: false — Swiper's default is to lock the slider
    // when slide widths sum < container width. At our init point, the
    // gallery's grid column hasn't always settled, so slides measure
    // 0px → Swiper thinks "everything fits" → locks navigation +
    // hides bullets + disables arrows even though there are real
    // multi-image slides. The static / single-slide branch above
    // already handles the "no slider needed" case, so we never need
    // Swiper's overflow auto-hide here.
    // observer + observeParents re-trigger Swiper's measurements when
    // the container resizes (customizer drawer toggles, viewport
    // changes, late-loading images settling layout).
    const mainOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: this.loop && slides.length > 1,
      speed: 400,
      watchOverflow: false,
      observer: true,
      observeParents: true,
    };

    if (this.autoplayEnabled) {
      mainOptions.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    if (this.showArrows && this.prevEl && this.nextEl) {
      mainOptions.navigation = {
        prevEl: this.prevEl,
        nextEl: this.nextEl,
      };
    }

    if (this.showBullets && this.bulletsEl) {
      mainOptions.pagination = {
        el: this.bulletsEl,
        clickable: true,
      };
    }

    if (this.thumbsSwiper) {
      mainOptions.thumbs = { swiper: this.thumbsSwiper };
    }

    this.mainSwiper = new Swiper(this.mainEl, mainOptions);

    // Initial active-thumb sync. Swiper's Thumbs module relies on
    // `slidesPerViewDynamic()`, which needs computed slide widths
    // to run — with `slidesPerView: 'auto'` on the thumbs strip,
    // those widths aren't measured yet at init, so `thumbs.update()`
    // silently bails. A manual classList toggle keyed off the main
    // swiper's `realIndex` (loop-safe — `activeIndex` would be off by
    // the loop clones' count) sidesteps the timing dependency. The
    // slideChange binding keeps the marker synced through every
    // navigation including the variant-routed jumps.
    if (this.thumbsSwiper) {
      const sync = () => {
        const i = this.mainSwiper.realIndex;
        this.thumbsEl
          .querySelectorAll('.product-main-gallery__thumb')
          .forEach((t, n) => t.classList.toggle('swiper-slide-thumb-active', n === i));
      };
      sync();
      this.mainSwiper.on('slideChange', sync);
    }

    this.root.classList.remove('not-loaded');
    this.root.classList.add('is-initialized');

    this._bindVariantUpdate();
    this._bindCustomizerSelect(slides);
  }
    _bindPhotoViewer() {
    const signal = this.abortController.signal;

    const photos = Array.from(
      this.mainEl.querySelectorAll(
        '.product-main-gallery__slide[data-media-type="image"]'
      )
    ).map((slide) => {
      const image = slide.querySelector('img');

      return {
        slide,
        image,
                isVariant:
          slide.hasAttribute('data-variant-id') &&
          slide.dataset.isCover !== 'true',
        src: image && (
          image.getAttribute('data_max_resolution') ||
          image.getAttribute('data-max-resolution') ||
          image.currentSrc ||
          image.src
        ),
      };
    }).filter((photo) => photo.image && photo.src);

    if (!photos.length) return;

    // Only non-variant photos belong in the browsing sequence.
    // Variant photos can still be opened directly.
    const galleryIndexes = [];

    photos.forEach((photo, index) => {
      if (!photo.isVariant) galleryIndexes.push(index);
    });

    const dialog = document.createElement('dialog');
    dialog.className = 'pdp-photo-viewer';
    dialog.setAttribute('aria-label', 'Product photos');

    const makeButton = (label, text, className) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.setAttribute('aria-label', label);
      button.textContent = text;
      return button;
    };

    const close = makeButton(
      'Close photo viewer', '×', 'pdp-photo-viewer__close'
    );
    close.autofocus = true;

    const previous = makeButton(
      'Previous photo', '‹', 'pdp-photo-viewer__previous'
    );

    const next = makeButton(
      'Next photo', '›', 'pdp-photo-viewer__next'
    );

    const stage = document.createElement('div');
    stage.className = 'pdp-photo-viewer__stage';

    const image = document.createElement('img');
    image.className = 'pdp-photo-viewer__image';
    image.draggable = false;

    // Keep the close button attached to the image container.
    stage.append(image, close);

    const count = document.createElement('p');
    count.className = 'pdp-photo-viewer__count';
    count.setAttribute('role', 'status');
    count.setAttribute('aria-live', 'polite');

    dialog.append(stage, previous, next, count);
    document.body.append(dialog);
    this.photoDialog = dialog;

    let index = 0;
    let opener = null;
    let oldOverflow = '';
    let resumeAutoplay = false;

    const positionClose = () => {
      if (!dialog.open) return;

      const imageBox = image.getBoundingClientRect();
      const stageBox = stage.getBoundingClientRect();

      if (!imageBox.width) return;

      close.style.top = `${imageBox.top - stageBox.top + 4}px`;
      close.style.right = `${stageBox.right - imageBox.right + 8}px`;
    };

    const displayPhoto = (photoIndex) => {
      index = photoIndex;

      const galleryPosition = galleryIndexes.indexOf(index);

      image.alt = photos[index].image.alt || 'Product photo';
      image.src = photos[index].src;

      count.textContent = galleryPosition >= 0
        ? `${galleryPosition + 1} / ${galleryIndexes.length}`
        : 'Variant photo';

      // With no main gallery photos, a variant opens on its own.
      // With one main photo, navigation is useful only from a variant.
      const canNavigate = galleryIndexes.length > 1 ||
        (galleryPosition < 0 && galleryIndexes.length > 0);

      previous.hidden = next.hidden = !canNavigate;

      requestAnimationFrame(positionClose);
    };

    const move = (direction) => {
      if (!galleryIndexes.length) return;

      const currentPosition = galleryIndexes.indexOf(index);

      // From a variant: next goes to the first main photo,
      // previous goes to the last main photo.
      const nextPosition = currentPosition < 0
        ? (direction > 0 ? 0 : galleryIndexes.length - 1)
        : (
            currentPosition + direction + galleryIndexes.length
          ) % galleryIndexes.length;

      displayPhoto(galleryIndexes[nextPosition]);
    };

        const open = (photoIndex, button) => {
      if (dialog.open) return;

      // Rebuild the sequence each time the viewer opens.
      // Keep the main gallery photos, plus the opening variant
      // photo if it is not already one of those main photos.
      const mainIndexes = [];

      photos.forEach((photo, index) => {
        if (!photo.isVariant) mainIndexes.push(index);
      });

      if (!mainIndexes.includes(photoIndex)) {
        mainIndexes.unshift(photoIndex);
      }

      galleryIndexes.splice(
        0,
        galleryIndexes.length,
        ...mainIndexes
      );

      opener = button;
      displayPhoto(photoIndex);


      oldOverflow = document.documentElement.style.overflow;
      resumeAutoplay = Boolean(this.mainSwiper?.autoplay?.running);

      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';

      if (resumeAutoplay) this.mainSwiper.autoplay.stop();

      requestAnimationFrame(positionClose);
    };

    close.addEventListener('click', () => dialog.close(), { signal });
    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === stage) {
        dialog.close();
      }
    }, { signal });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    }, { signal });

    image.addEventListener('load', positionClose, { signal });
    window.addEventListener('resize', positionClose, { signal });

    let touchStart = null;

    stage.addEventListener('touchstart', (event) => {
      touchStart = event.touches.length === 1
        ? {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
          }
        : null;
    }, { passive: true, signal });

    stage.addEventListener('touchend', (event) => {
      if (!touchStart || !event.changedTouches.length) return;

      const touch = event.changedTouches[0];
      const dx = touch.clientX - touchStart.x;
      const dy = touch.clientY - touchStart.y;
      touchStart = null;

      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        move(dx < 0 ? 1 : -1);
      }
    }, { passive: true, signal });

    stage.addEventListener('touchcancel', () => {
      touchStart = null;
    }, { signal });

    // Keep this listener available if a section unload closes
    // and removes the dialog before its close event is delivered.
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = oldOverflow;

      if (resumeAutoplay && this.mainSwiper?.autoplay) {
        this.mainSwiper.autoplay.start();
      }

      if (opener?.isConnected) {
        opener.focus({ preventScroll: true });
      }
    });

    this.photoOpeners = [];

    photos.forEach((photo, photoIndex) => {
      const button = makeButton(
        `Enlarge photo ${photoIndex + 1}`,
        '',
        'pdp-photo-viewer__opener'
      );

      button.setAttribute('aria-haspopup', 'dialog');

      button.addEventListener('click', (event) => {
        if (this.mainSwiper && !this.mainSwiper.allowClick) return;

        event.preventDefault();
        open(photoIndex, button);
      }, { signal });

      photo.slide.append(button);
      this.photoOpeners.push(button);
    });
  }

  _bindVariantUpdate() {
    // Listen for H3.0's variant:update event and navigate the main
    // slider to the slide tied to the newly-selected variant. Scope
    // by .shopify-section so multiple PDPs on the page don't bleed.
    const section = this.root.closest('.shopify-section');
    const target = section || document;

    target.addEventListener(
      'variant:update',
      (event) => {
        const detail = event && event.detail;
        const resource = detail && detail.resource;
        const variantId = resource && resource.id;
        if (!variantId) return;
        this._goToVariant(String(variantId));
      },
      { signal: this.abortController.signal }
    );
  }

  _goToVariant(variantId) {
        const slide = this.mainEl.querySelector(
      `.swiper-slide[data-variant-ids~="${variantId}"]`
    );
    if (!slide) return;

    if (this.mainSwiper) {
      // For loop mode use the data-swiper-slide-index attribute Swiper
      // stamps on each real slide so slideToLoop targets the original
      // index rather than the duplicated clones.
      if (this.mainSwiper.params.loop) {
        const loopIndex = parseInt(slide.dataset.swiperSlideIndex, 10);
        if (!Number.isNaN(loopIndex)) {
          this.mainSwiper.slideToLoop(loopIndex, 0);
          return;
        }
      }
      const slides = Array.from(this.mainEl.querySelectorAll('.swiper-slide'));
      const idx = slides.indexOf(slide);
      if (idx >= 0) this.mainSwiper.slideTo(idx, 0);
    } else {
      // Static stack mode (desktop scroll). Scroll the matching slide
      // into view smoothly.
      slide.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  _bindCustomizerSelect(slides) {
    // shopify:block:select — merchant clicked a block in the
    // customizer tree. We don't have addressable slides in our schema
    // (media is product data, not blocks), so this is a no-op for
    // now. Wired here so we have a single place to extend if the
    // gallery later exposes slide-like blocks.
  }

    destroy() {
    if (this.photoDialog?.open) this.photoDialog.close();
    this.photoDialog?.remove();
    this.photoOpeners?.forEach((button) => button.remove());

    this.abortController.abort();
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductMainGallery(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));

